import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditorStore } from '@/store/editorStore';
import { useDocumentStore } from '@/store/documentStore';
import { useIsMobile } from '@/hooks/useMediaQuery';
import type { Attachment } from '@/model/types';
import {
  requestUploadUrl,
  uploadFile,
  getDownloadUrl,
  deleteAttachment,
} from '@/api/attachments';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImageMime(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

function getTypeIcon(mimeType: string): string {
  if (mimeType.startsWith('image/')) return 'img';
  if (mimeType.startsWith('video/')) return 'vid';
  if (mimeType.startsWith('audio/')) return 'aud';
  if (mimeType.includes('pdf')) return 'pdf';
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'xls';
  if (mimeType.includes('document') || mimeType.includes('word')) return 'doc';
  return 'file';
}

function isValidUrl(text: string): boolean {
  try {
    const url = new URL(text);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

// panelStyle is now computed inside AttachmentPanel to support responsive layout

const sectionStyle: React.CSSProperties = {
  background: 'var(--bg-surface)',
  borderRadius: 6,
  margin: '0 12px 12px',
  padding: 12,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 8px',
  background: 'var(--bg-app)',
  color: 'var(--text-primary)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 12,
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box',
};

const smallBtnStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--text-tertiary)',
  cursor: 'pointer',
  fontSize: 12,
  padding: '2px 6px',
  borderRadius: 3,
};

interface AttachmentPanelProps {
  mapId: string | null;
}

export function AttachmentPanel({ mapId }: AttachmentPanelProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const attachmentPanelNodeId = useEditorStore((s) => s.attachmentPanelNodeId);
  const closeAttachmentPanel = useEditorStore((s) => s.closeAttachmentPanel);
  const nodes = useDocumentStore((s) => s.nodes);
  const mapDoc = useDocumentStore((s) => s.mapDoc);

  const node = attachmentPanelNodeId
    ? nodes.get(attachmentPanelNodeId)
    : undefined;

  // Link form state
  const [linkUrl, setLinkUrl] = useState('');
  const [linkLabel, setLinkLabel] = useState('');

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Thumbnail URL cache
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>({});

  // Reset form state when panel opens for a different node
  useEffect(() => {
    setLinkUrl('');
    setLinkLabel('');
    setUploadError(null);
    setThumbnailUrls({});
  }, [attachmentPanelNodeId]);

  // Load thumbnail URLs for image attachments
  useEffect(() => {
    if (!node?.attachments || !mapId) return;
    const imageAttachments = node.attachments.filter((a) => isImageMime(a.mimeType));
    for (const att of imageAttachments) {
      if (!thumbnailUrls[att.id]) {
        void getDownloadUrl(mapId, att.id, att.s3Key).then((url) => {
          setThumbnailUrls((prev) => ({ ...prev, [att.id]: url }));
        });
      }
    }
  }, [node?.attachments, thumbnailUrls, mapId]);

  const handleAddLink = useCallback(() => {
    if (!attachmentPanelNodeId || !linkUrl.trim()) return;
    const url = linkUrl.trim();
    const label = linkLabel.trim() || undefined;
    mapDoc.addNodeLink(attachmentPanelNodeId, url, label);
    setLinkUrl('');
    setLinkLabel('');
  }, [attachmentPanelNodeId, linkUrl, linkLabel, mapDoc]);

  const handleRemoveLink = useCallback(
    (url: string) => {
      if (!attachmentPanelNodeId) return;
      mapDoc.removeNodeLink(attachmentPanelNodeId, url);
    },
    [attachmentPanelNodeId, mapDoc],
  );

  const handleLinkUrlPaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pasted = e.clipboardData.getData('text');
      if (isValidUrl(pasted) && !linkLabel) {
        // Auto-detect URL paste -- keep label empty so user can optionally fill it
      }
    },
    [linkLabel],
  );

  const handleUploadFiles = useCallback(
    async (files: FileList | File[]) => {
      if (!attachmentPanelNodeId || !mapId) return;
      setUploadError(null);
      setUploading(true);
      setUploadProgress(0);

      const fileArray = Array.from(files);
      const total = fileArray.length;
      let completed = 0;

      for (const file of fileArray) {
        try {
          const { attachmentId, uploadUrl, s3Key } = await requestUploadUrl(
            mapId,
            file.name,
            file.type,
            file.size,
          );
          await uploadFile(uploadUrl, file);

          const attachment: Attachment = {
            id: attachmentId,
            fileName: file.name,
            mimeType: file.type,
            s3Key,
            sizeBytes: file.size,
            uploadedAt: new Date().toISOString(),
          };
          mapDoc.addNodeAttachment(attachmentPanelNodeId, attachment);

          completed++;
          setUploadProgress(Math.round((completed / total) * 100));
        } catch (err) {
          setUploadError(
            t('panels.uploadFailed', {
              fileName: file.name,
              error: err instanceof Error ? err.message : 'Unknown error',
            }),
          );
          break;
        }
      }

      setUploading(false);
    },
    [attachmentPanelNodeId, mapDoc, mapId, t],
  );

  const handleFileSelect = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        void handleUploadFiles(e.target.files);
        e.target.value = '';
      }
    },
    [handleUploadFiles],
  );

  const handleDownload = useCallback(async (att: Attachment) => {
    if (!mapId) return;
    try {
      const url = await getDownloadUrl(mapId, att.id, att.s3Key);
      window.open(url, '_blank');
    } catch {
      // Silently fail -- user can retry
    }
  }, [mapId]);

  const handleDeleteAttachment = useCallback(
    async (att: Attachment) => {
      if (!attachmentPanelNodeId || !mapId) return;
      try {
        await deleteAttachment(mapId, att.id, att.s3Key);
      } catch {
        // Continue to remove from document even if server delete fails
      }
      mapDoc.removeNodeAttachment(attachmentPanelNodeId, att.id);
    },
    [attachmentPanelNodeId, mapDoc, mapId],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      if (e.dataTransfer.files.length > 0) {
        void handleUploadFiles(e.dataTransfer.files);
      }
    },
    [handleUploadFiles],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeAttachmentPanel();
      }
    },
    [closeAttachmentPanel],
  );

  const handleLinkKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAddLink();
      }
    },
    [handleAddLink],
  );

  if (!attachmentPanelNodeId || !node) return null;

  const links = node.links ?? [];
  const attachments = node.attachments ?? [];

  const panelStyle: React.CSSProperties = {
    position: isMobile ? 'fixed' : 'absolute',
    top: isMobile ? 0 : 50,
    bottom: isMobile ? 0 : 28,
    right: 0,
    width: isMobile ? '100%' : 320,
    zIndex: isMobile ? 35 : 20,
    background: 'var(--bg-app)',
    borderLeft: isMobile ? 'none' : '1px solid var(--border-subtle)',
    fontFamily: 'var(--font-family)',
    display: 'flex',
    flexDirection: 'column',
    animation: 'attachPanelSlideIn 150ms ease-out',
  };

  return (
    <>
    {isMobile && (
      <div
        onClick={closeAttachmentPanel}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'var(--bg-overlay)',
          zIndex: 34,
        }}
      />
    )}
    <div style={panelStyle} onKeyDown={handleKeyDown} tabIndex={-1}>
      <style>{`
        @keyframes attachPanelSlideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>

      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          paddingTop: isMobile ? 'var(--space-4)' : 12,
          borderBottom: '1px solid var(--border-subtle)',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            color: 'var(--text-secondary)',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {t('panels.attachments')}
        </span>
        <button
          onClick={closeAttachmentPanel}
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-tertiary)',
            cursor: 'pointer',
            fontSize: 'var(--font-size-lg)',
            padding: '0 4px',
            lineHeight: 1,
          }}
          title={t('panels.closePanel', { panel: 'attachment' })}
        >
          x
        </button>
      </div>

      {/* Node title */}
      <div
        style={{
          padding: '8px 16px',
          color: 'var(--text-primary)',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {node.text || t('panels.emptyNode')}
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 0 12px' }}>
        {/* Links Section */}
        <div
          style={{
            padding: '4px 16px 8px',
            color: 'var(--text-secondary)',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {t('panels.links', { count: links.length })}
        </div>
        <div style={sectionStyle}>
          {/* Existing links */}
          {links.map((link, idx) => (
            <div
              key={`${link.url}-${idx}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 6,
              }}
            >
              <a
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  flex: 1,
                  color: 'var(--text-link)',
                  fontSize: 12,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  textDecoration: 'none',
                }}
                title={link.url}
              >
                {link.label || link.url}
              </a>
              <button
                onClick={() => handleRemoveLink(link.url)}
                style={smallBtnStyle}
                title={t('panels.removeLink')}
              >
                x
              </button>
            </div>
          ))}

          {/* Add link form */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: links.length > 0 ? 8 : 0 }}>
            <input
              type="text"
              placeholder={t('panels.url')}
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onPaste={handleLinkUrlPaste}
              onKeyDown={handleLinkKeyDown}
              style={inputStyle}
            />
            <input
              type="text"
              placeholder={t('panels.labelOptional')}
              value={linkLabel}
              onChange={(e) => setLinkLabel(e.target.value)}
              onKeyDown={handleLinkKeyDown}
              style={inputStyle}
            />
            <button
              onClick={handleAddLink}
              disabled={!linkUrl.trim()}
              style={{
                padding: '5px 10px',
                background: linkUrl.trim() ? 'var(--color-primary-hover)' : 'var(--bg-surface-raised)',
                color: linkUrl.trim() ? '#fff' : 'var(--text-tertiary)',
                border: 'none',
                borderRadius: 'var(--radius-sm)',
                fontSize: 12,
                cursor: linkUrl.trim() ? 'pointer' : 'default',
                fontFamily: 'inherit',
              }}
            >
              {t('panels.addLink')}
            </button>
          </div>
        </div>

        {/* Attachments Section */}
        <div
          style={{
            padding: '4px 16px 8px',
            color: 'var(--text-secondary)',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {t('panels.files', { count: attachments.length })}
        </div>
        <div style={sectionStyle}>
          {/* Existing attachments */}
          {attachments.map((att) => (
            <div
              key={att.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 8,
              }}
            >
              {/* Thumbnail for images */}
              {isImageMime(att.mimeType) && thumbnailUrls[att.id] ? (
                <img
                  src={thumbnailUrls[att.id]}
                  alt={att.fileName}
                  style={{
                    width: 32,
                    height: 32,
                    objectFit: 'cover',
                    borderRadius: 3,
                    flexShrink: 0,
                  }}
                />
              ) : (
                <span
                  style={{
                    width: 32,
                    height: 32,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'var(--bg-app)',
                    borderRadius: 3,
                    fontSize: 9,
                    color: 'var(--text-secondary)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    flexShrink: 0,
                  }}
                >
                  {getTypeIcon(att.mimeType)}
                </span>
              )}
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <div
                  style={{
                    color: 'var(--text-primary)',
                    fontSize: 12,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {att.fileName}
                </div>
                <div style={{ color: 'var(--text-tertiary)', fontSize: 10 }}>
                  {formatFileSize(att.sizeBytes)}
                </div>
              </div>
              <button
                onClick={() => void handleDownload(att)}
                style={smallBtnStyle}
                title={t('panels.download')}
              >
                dl
              </button>
              <button
                onClick={() => void handleDeleteAttachment(att)}
                style={smallBtnStyle}
                title={t('panels.delete')}
              >
                x
              </button>
            </div>
          ))}

          {/* Upload progress */}
          {uploading && (
            <div style={{ marginBottom: 8 }}>
              <div
                style={{
                  height: 4,
                  background: 'var(--bg-app)',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${uploadProgress}%`,
                    height: '100%',
                    background: 'var(--color-primary-hover)',
                    transition: 'width 200ms ease',
                  }}
                />
              </div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 10, marginTop: 2 }}>
                {t('panels.uploading', { progress: uploadProgress })}
              </div>
            </div>
          )}

          {/* Upload error */}
          {uploadError && (
            <div
              style={{
                color: 'var(--text-error)',
                fontSize: 'var(--font-size-xs)',
                marginBottom: 8,
                padding: '4px 6px',
                background: 'var(--bg-error-subtle)',
                borderRadius: 3,
              }}
            >
              {uploadError}
            </div>
          )}

          {/* Drop zone / Upload button */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            style={{
              border: `2px dashed ${dragOver ? 'var(--color-primary-hover)' : 'var(--border-default)'}`,
              borderRadius: 6,
              padding: '16px 8px',
              textAlign: 'center',
              color: dragOver ? 'var(--text-link)' : 'var(--text-tertiary)',
              fontSize: 12,
              cursor: 'pointer',
              transition: 'border-color 150ms, color 150ms',
              background: dragOver ? 'var(--bg-active)' : 'transparent',
            }}
            onClick={handleFileSelect}
          >
            {t('panels.dropFiles')}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileInputChange}
          />
        </div>
      </div>
    </div>
    </>
  );
}
